import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, DollarSign, BookOpen, Trophy, Download, Upload,
  Search, Loader2, RefreshCw, ChevronLeft, ChevronRight, Crown, Sparkles, Building, Clock,
  Plus, Trash2, Edit2, Save, X, CreditCard, Eye, Key, UserPlus, School, Church, GraduationCap,
  MessageSquare, HelpCircle, AlertCircle, CheckCircle, Clock3, Send, FileText
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AdminDashboard = () => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pricing state
  const [pricingPlans, setPricingPlans] = useState([]);
  const [orgPricing, setOrgPricing] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [newPlan, setNewPlan] = useState({ plan_id: '', name: '', price: '', features: '' });
  const [newTier, setNewTier] = useState({ min_students: '', max_students: '', price_per_student: '', description: '' });
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [showNewTierForm, setShowNewTierForm] = useState(false);
  
  // Organization Management State
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgTypeFilter, setOrgTypeFilter] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  const [viewOrgDialog, setViewOrgDialog] = useState(false);
  
  // Create Organization Dialog
  const [createOrgDialog, setCreateOrgDialog] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    organization_type: 'school',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    student_licenses: 50,
    contact_email: '',
    contact_phone: ''
  });
  const [creatingOrg, setCreatingOrg] = useState(false);
  
  // Add User to Org Dialog
  const [addUserDialog, setAddUserDialog] = useState({ open: false, orgId: null, orgName: '' });
  const [newOrgUser, setNewOrgUser] = useState({
    email: '',
    name: '',
    password: '',
    account_type: 'teacher'
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Reset Password Dialog
  const [resetPwdDialog, setResetPwdDialog] = useState({ open: false, orgId: null, userId: null, userName: '' });
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);
  
  // Delete Org Dialog
  const [deleteOrgDialog, setDeleteOrgDialog] = useState({ open: false, orgId: null, orgName: '' });
  const [deletingOrg, setDeletingOrg] = useState(false);
  
  // Support Tickets State
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketFilter, setTicketFilter] = useState({ status: 'all', category: 'all', priority: 'all' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [respondingTicket, setRespondingTicket] = useState(false);
  
  // FAQ State
  const [faqArticles, setFaqArticles] = useState([]);
  const [faqStats, setFaqStats] = useState({ total: 0, published: 0, draft: 0 });
  const [faqLoading, setFaqLoading] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: 'general', order: 0, is_published: true });
  const [showNewFaqForm, setShowNewFaqForm] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    initializeData();
    fetchPricing();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(debounce);
  }, [page, searchQuery]);

  const initializeData = async () => {
    try {
      // Initialize badges and activities
      await api.post('/admin/init-badges');
      await api.post('/admin/init-activities');
    } catch (error) {
      console.log('Init data:', error.message);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get(`/admin/users?page=${page}&limit=10&search=${searchQuery}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleExport = async (collection) => {
    setExporting(collection);
    try {
      const response = await api.get(`/admin/export/${collection}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collection}_export.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${collection} exported successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    try {
      await api.put(`/admin/users/${userId}/subscription?plan=${plan}`);
      toast.success('Subscription updated');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  // Pricing management functions
  const fetchPricing = async () => {
    try {
      const [plansRes, orgRes] = await Promise.all([
        api.get('/admin/pricing/plans'),
        api.get('/admin/pricing/organization')
      ]);
      setPricingPlans(plansRes.data);
      setOrgPricing(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const handleUpdatePlan = async (planId, updates) => {
    try {
      const params = new URLSearchParams();
      if (updates.name) params.append('name', updates.name);
      if (updates.price) params.append('price', updates.price);
      if (updates.features) params.append('features', updates.features);
      
      await api.put(`/admin/pricing/plans/${planId}?${params.toString()}`);
      toast.success('Plan updated');
      fetchPricing();
      setEditingPlan(null);
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.plan_id || !newPlan.name || !newPlan.price) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const features = newPlan.features.split(',').map(f => f.trim()).filter(f => f);
      await api.post(`/admin/pricing/plans?plan_id=${newPlan.plan_id}&name=${encodeURIComponent(newPlan.name)}&price=${newPlan.price}&features=${encodeURIComponent(JSON.stringify(features))}`);
      toast.success('Plan created');
      fetchPricing();
      setNewPlan({ plan_id: '', name: '', price: '', features: '' });
      setShowNewPlanForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await api.delete(`/admin/pricing/plans/${planId}`);
      toast.success('Plan deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleUpdateOrgTier = async (tierId, updates) => {
    try {
      const params = new URLSearchParams();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, value);
      });
      
      await api.put(`/admin/pricing/organization/${tierId}?${params.toString()}`);
      toast.success('Pricing tier updated');
      fetchPricing();
      setEditingTier(null);
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const handleCreateOrgTier = async () => {
    if (!newTier.min_students || !newTier.price_per_student || !newTier.description) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const params = new URLSearchParams({
        min_students: newTier.min_students,
        price_per_student: newTier.price_per_student,
        description: newTier.description
      });
      if (newTier.max_students) params.append('max_students', newTier.max_students);
      
      await api.post(`/admin/pricing/organization?${params.toString()}`);
      toast.success('Pricing tier created');
      fetchPricing();
      setNewTier({ min_students: '', max_students: '', price_per_student: '', description: '' });
      setShowNewTierForm(false);
    } catch (error) {
      toast.error('Failed to create tier');
    }
  };

  const handleDeleteOrgTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this pricing tier?')) return;
    try {
      await api.delete(`/admin/pricing/organization/${tierId}`);
      toast.success('Pricing tier deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  // ==================== ORGANIZATION MANAGEMENT ====================
  
  const fetchOrganizations = async () => {
    setOrgsLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgSearch) params.append('search', orgSearch);
      if (orgTypeFilter && orgTypeFilter !== 'all') params.append('org_type', orgTypeFilter);
      
      const response = await api.get(`/admin/organizations?${params.toString()}`);
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setOrgsLoading(false);
    }
  };
  
  const fetchOrgDetails = async (orgId) => {
    setOrgDetailLoading(true);
    try {
      const response = await api.get(`/admin/organizations/${orgId}`);
      setSelectedOrg(response.data);
      setViewOrgDialog(true);
    } catch (error) {
      toast.error('Failed to load organization details');
    } finally {
      setOrgDetailLoading(false);
    }
  };
  
  const handleCreateOrganization = async () => {
    if (!newOrg.name || !newOrg.admin_email || !newOrg.admin_name || !newOrg.admin_password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setCreatingOrg(true);
    try {
      await api.post('/admin/organizations', {
        name: newOrg.name,
        organization_type: newOrg.organization_type,
        admin_email: newOrg.admin_email,
        admin_name: newOrg.admin_name,
        admin_password: newOrg.admin_password,
        student_licenses: parseInt(newOrg.student_licenses) || 50,
        contact_email: newOrg.contact_email || newOrg.admin_email,
        contact_phone: newOrg.contact_phone || null
      });
      
      toast.success('Organization created successfully');
      setCreateOrgDialog(false);
      setNewOrg({
        name: '',
        organization_type: 'school',
        admin_email: '',
        admin_name: '',
        admin_password: '',
        student_licenses: 50,
        contact_email: '',
        contact_phone: ''
      });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };
  
  const handleAddUserToOrg = async () => {
    if (!newOrgUser.email || !newOrgUser.name || !newOrgUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setAddingUser(true);
    try {
      await api.post(`/admin/organizations/${addUserDialog.orgId}/users`, newOrgUser);
      toast.success('User added to organization');
      setAddUserDialog({ open: false, orgId: null, orgName: '' });
      setNewOrgUser({ email: '', name: '', password: '', account_type: 'teacher' });
      // Refresh org details if dialog is open
      if (selectedOrg && selectedOrg.organization.id === addUserDialog.orgId) {
        fetchOrgDetails(addUserDialog.orgId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };
  
  const handleResetOrgUserPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPwd(true);
    try {
      await api.post(`/admin/organizations/${resetPwdDialog.orgId}/reset-password/${resetPwdDialog.userId}?new_password=${encodeURIComponent(resetPassword)}`);
      toast.success('Password reset successfully');
      setResetPwdDialog({ open: false, orgId: null, userId: null, userName: '' });
      setResetPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResettingPwd(false);
    }
  };
  
  const handleDeleteOrganization = async () => {
    setDeletingOrg(true);
    try {
      await api.delete(`/admin/organizations/${deleteOrgDialog.orgId}`);
      toast.success('Organization deleted successfully');
      setDeleteOrgDialog({ open: false, orgId: null, orgName: '' });
      setViewOrgDialog(false);
      setSelectedOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to delete organization');
    } finally {
      setDeletingOrg(false);
    }
  };
  
  const handleRemoveUserFromOrg = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    
    try {
      await api.delete(`/admin/organizations/${selectedOrg.organization.id}/users/${userId}`);
      toast.success('User removed from organization');
      fetchOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };
  
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/organizations/${selectedOrg.organization.id}/users/${userId}/role?new_role=${newRole}`);
      toast.success('User role updated');
      fetchOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // Fetch orgs when tab changes or search changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchOrganizations();
    }, 300);
    return () => clearTimeout(debounce);
  }, [orgSearch, orgTypeFilter]);

  // ==================== SUPPORT TICKETS FUNCTIONS ====================
  
  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = new URLSearchParams();
      if (ticketFilter.status && ticketFilter.status !== 'all') params.append('status', ticketFilter.status);
      if (ticketFilter.category && ticketFilter.category !== 'all') params.append('category', ticketFilter.category);
      if (ticketFilter.priority && ticketFilter.priority !== 'all') params.append('priority', ticketFilter.priority);
      
      const response = await api.get(`/admin/support/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
      setTicketStats(response.data.stats || { total: 0, open: 0, in_progress: 0, resolved: 0 });
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };
  
  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await api.put(`/admin/support/tickets/${ticketId}`, updates);
      toast.success('Ticket updated');
      fetchTickets();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };
  
  const handleRespondToTicket = async () => {
    if (!ticketResponse.trim()) {
      toast.error('Please enter a response');
      return;
    }
    setRespondingTicket(true);
    try {
      await api.put(`/admin/support/tickets/${selectedTicket.id}`, {
        admin_response: ticketResponse,
        status: 'in_progress'
      });
      toast.success('Response sent');
      fetchTickets();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setRespondingTicket(false);
    }
  };
  
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await api.delete(`/admin/support/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'support') {
      fetchTickets();
    }
  }, [activeTab, ticketFilter]);
  
  // ==================== FAQ FUNCTIONS ====================
  
  const fetchFaq = async () => {
    setFaqLoading(true);
    try {
      const response = await api.get('/admin/faq');
      setFaqArticles(response.data.articles || []);
      setFaqStats(response.data.stats || { total: 0, published: 0, draft: 0 });
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
    } finally {
      setFaqLoading(false);
    }
  };
  
  const handleCreateFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast.error('Please fill in question and answer');
      return;
    }
    setSavingFaq(true);
    try {
      await api.post('/admin/faq', newFaq);
      toast.success('FAQ article created');
      fetchFaq();
      setNewFaq({ question: '', answer: '', category: 'general', order: 0, is_published: true });
      setShowNewFaqForm(false);
    } catch (error) {
      toast.error('Failed to create FAQ');
    } finally {
      setSavingFaq(false);
    }
  };
  
  const handleUpdateFaq = async (faqId, updates) => {
    try {
      await api.put(`/admin/faq/${faqId}`, updates);
      toast.success('FAQ updated');
      fetchFaq();
      setEditingFaq(null);
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };
  
  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await api.delete(`/admin/faq/${faqId}`);
      toast.success('FAQ deleted');
      fetchFaq();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };
  
  const handleSeedFaq = async () => {
    try {
      const response = await api.post('/admin/faq/seed');
      toast.success(response.data.message || `Seeded ${response.data.seeded_count} FAQ articles`);
      fetchFaq();
    } catch (error) {
      toast.error('Failed to seed FAQ');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'faq') {
      fetchFaq();
    }
  }, [activeTab]);

  const COLORS = ['#64748B', '#3B82F6', '#8B5CF6'];

  const subscriptionData = stats ? [
    { name: 'Free', value: stats.subscriptions.free },
    { name: 'Basic', value: stats.subscriptions.basic },
    { name: 'Premium', value: stats.subscriptions.premium },
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 theme-parents">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-yellow-500" />
                <h1 className="font-bold text-xl text-slate-800">Admin Dashboard</h1>
              </div>
            </div>
            
            <Button variant="outline" onClick={() => { fetchStats(); fetchUsers(); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('users')}
            data-testid="users-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('organizations')}
            data-testid="children-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Children</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_children || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('overview')}
            data-testid="activities-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Activities Completed</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_activities_completed || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('pricing')}
            data-testid="revenue-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-slate-900">${stats?.total_revenue || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Support Tickets Widget */}
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('support')}
            data-testid="support-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Open Tickets</p>
                  <p className="text-2xl font-bold text-slate-900">{ticketStats.open || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* FAQ Widget */}
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('faq')}
            data-testid="faq-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">FAQ Articles</p>
                  <p className="text-2xl font-bold text-slate-900">{faqStats.published || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Subscription Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subscriptionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {subscriptionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                {subscriptionData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                    <span className="text-sm text-slate-600">{entry.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Export Data</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {['users', 'children', 'activity_progress', 'assessments', 'payment_transactions'].map((collection) => (
                  <Button
                    key={collection}
                    variant="outline"
                    className="justify-start"
                    onClick={() => handleExport(collection)}
                    disabled={exporting === collection}
                    data-testid={`export-${collection}-btn`}
                  >
                    {exporting === collection ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {collection.replace('_', ' ')}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>User Management</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-9 w-64"
                    data-testid="user-search-input"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                              user.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {user.subscription_plan || 'free'}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={user.subscription_plan || 'free'}
                              onValueChange={(value) => handleUpdateSubscription(user.id, value)}
                            >
                              <SelectTrigger className="w-28" data-testid={`plan-select-${user.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="basic">Basic</SelectItem>
                                <SelectItem value="premium">Premium</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent Registrations */}
        {stats?.recent_registrations?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Recent Registrations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_registrations.slice(0, 5).map((user) => (
                  <div key={user.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center text-sky-600 font-bold">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <span className="text-sm text-slate-400">
                      {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Management Section */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Pricing Management
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="individual" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="individual" data-testid="tab-individual-pricing">Individual Plans</TabsTrigger>
                <TabsTrigger value="organization" data-testid="tab-org-pricing">Organization Pricing</TabsTrigger>
              </TabsList>
              
              {/* Individual Plans */}
              <TabsContent value="individual">
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowNewPlanForm(!showNewPlanForm)}
                      className="gap-2"
                      data-testid="add-plan-btn"
                    >
                      <Plus className="w-4 h-4" /> Add Plan
                    </Button>
                  </div>
                  
                  {showNewPlanForm && (
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-3">New Pricing Plan</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            placeholder="Plan ID (e.g., starter)"
                            value={newPlan.plan_id}
                            onChange={(e) => setNewPlan({ ...newPlan, plan_id: e.target.value })}
                            data-testid="new-plan-id"
                          />
                          <Input
                            placeholder="Plan Name"
                            value={newPlan.name}
                            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                            data-testid="new-plan-name"
                          />
                          <Input
                            type="number"
                            placeholder="Price (USD)"
                            value={newPlan.price}
                            onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                            data-testid="new-plan-price"
                          />
                          <Input
                            placeholder="Features (comma separated)"
                            value={newPlan.features}
                            onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })}
                            data-testid="new-plan-features"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button variant="ghost" onClick={() => setShowNewPlanForm(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleCreatePlan} data-testid="save-new-plan-btn">
                            <Save className="w-4 h-4 mr-2" /> Create Plan
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Features</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pricingPlans.map((plan) => (
                        <TableRow key={plan.plan_id}>
                          <TableCell className="font-medium">{plan.plan_id}</TableCell>
                          <TableCell>
                            {editingPlan === plan.plan_id ? (
                              <Input
                                defaultValue={plan.name}
                                id={`edit-name-${plan.plan_id}`}
                                className="w-32"
                              />
                            ) : plan.name}
                          </TableCell>
                          <TableCell>
                            {editingPlan === plan.plan_id ? (
                              <Input
                                type="number"
                                defaultValue={plan.price}
                                id={`edit-price-${plan.plan_id}`}
                                className="w-24"
                              />
                            ) : `$${plan.price}`}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="text-sm text-slate-600 truncate">
                              {plan.features?.join(', ')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {editingPlan === plan.plan_id ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const name = document.getElementById(`edit-name-${plan.plan_id}`)?.value;
                                      const price = document.getElementById(`edit-price-${plan.plan_id}`)?.value;
                                      handleUpdatePlan(plan.plan_id, { name, price });
                                    }}
                                    data-testid={`save-plan-${plan.plan_id}`}
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingPlan(null)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingPlan(plan.plan_id)}
                                    data-testid={`edit-plan-${plan.plan_id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDeletePlan(plan.plan_id)}
                                    data-testid={`delete-plan-${plan.plan_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              
              {/* Organization Pricing */}
              <TabsContent value="organization">
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowNewTierForm(!showNewTierForm)}
                      className="gap-2"
                      data-testid="add-tier-btn"
                    >
                      <Plus className="w-4 h-4" /> Add Tier
                    </Button>
                  </div>
                  
                  {showNewTierForm && (
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-3">New Organization Pricing Tier</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            type="number"
                            placeholder="Min Students"
                            value={newTier.min_students}
                            onChange={(e) => setNewTier({ ...newTier, min_students: e.target.value })}
                            data-testid="new-tier-min"
                          />
                          <Input
                            type="number"
                            placeholder="Max Students (optional)"
                            value={newTier.max_students}
                            onChange={(e) => setNewTier({ ...newTier, max_students: e.target.value })}
                            data-testid="new-tier-max"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price per Student (USD)"
                            value={newTier.price_per_student}
                            onChange={(e) => setNewTier({ ...newTier, price_per_student: e.target.value })}
                            data-testid="new-tier-price"
                          />
                          <Input
                            placeholder="Description"
                            value={newTier.description}
                            onChange={(e) => setNewTier({ ...newTier, description: e.target.value })}
                            data-testid="new-tier-desc"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button variant="ghost" onClick={() => setShowNewTierForm(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleCreateOrgTier} data-testid="save-new-tier-btn">
                            <Save className="w-4 h-4 mr-2" /> Create Tier
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student Range</TableHead>
                        <TableHead>Price/Student</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orgPricing.map((tier) => (
                        <TableRow key={tier.id}>
                          <TableCell className="font-medium">
                            {editingTier === tier.id ? (
                              <div className="flex gap-2">
                                <Input
                                  type="number"
                                  defaultValue={tier.min_students}
                                  id={`edit-min-${tier.id}`}
                                  className="w-20"
                                />
                                <span>-</span>
                                <Input
                                  type="number"
                                  defaultValue={tier.max_students || ''}
                                  placeholder="∞"
                                  id={`edit-max-${tier.id}`}
                                  className="w-20"
                                />
                              </div>
                            ) : (
                              `${tier.min_students} - ${tier.max_students || '∞'}`
                            )}
                          </TableCell>
                          <TableCell>
                            {editingTier === tier.id ? (
                              <Input
                                type="number"
                                step="0.01"
                                defaultValue={tier.price_per_student}
                                id={`edit-price-tier-${tier.id}`}
                                className="w-24"
                              />
                            ) : `$${tier.price_per_student}`}
                          </TableCell>
                          <TableCell>
                            {editingTier === tier.id ? (
                              <Input
                                defaultValue={tier.description}
                                id={`edit-desc-${tier.id}`}
                                className="w-40"
                              />
                            ) : tier.description}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {editingTier === tier.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const min_students = document.getElementById(`edit-min-${tier.id}`)?.value;
                                      const max_students = document.getElementById(`edit-max-${tier.id}`)?.value;
                                      const price_per_student = document.getElementById(`edit-price-tier-${tier.id}`)?.value;
                                      const description = document.getElementById(`edit-desc-${tier.id}`)?.value;
                                      handleUpdateOrgTier(tier.id, { min_students, max_students, price_per_student, description });
                                    }}
                                    data-testid={`save-tier-${tier.id}`}
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingTier(null)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingTier(tier.id)}
                                    data-testid={`edit-tier-${tier.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteOrgTier(tier.id)}
                                    data-testid={`delete-tier-${tier.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Organization Management Section */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-indigo-600" />
                Organization Management
              </CardTitle>
              <Button onClick={() => setCreateOrgDialog(true)} data-testid="create-org-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Organization
              </Button>
            </div>
            <CardDescription>Manage schools, churches, and other organizations on the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search organizations..."
                  value={orgSearch}
                  onChange={(e) => setOrgSearch(e.target.value)}
                  className="pl-9"
                  data-testid="org-search-input"
                />
              </div>
              <Select value={orgTypeFilter} onValueChange={setOrgTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="org-type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="sunday_school">Sunday School</SelectItem>
                  <SelectItem value="church">Church</SelectItem>
                  <SelectItem value="homeschool">Homeschool Co-op</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchOrganizations} data-testid="refresh-orgs-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Organizations Table */}
            {orgsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Teachers</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Classes</TableHead>
                    <TableHead>Licenses</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No organizations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    organizations.map((org) => (
                      <TableRow key={org.id} className="cursor-pointer hover:bg-slate-50" onClick={() => window.location.href = `/admin/organization/${org.id}`}>
                        <TableCell>
                          <Link to={`/admin/organization/${org.id}`} className="flex items-center gap-3 hover:text-emerald-600" onClick={(e) => e.stopPropagation()} data-testid={`org-link-${org.id}`}>
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              org.organization_type === 'church' || org.organization_type === 'sunday_school' 
                                ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              {org.organization_type === 'church' || org.organization_type === 'sunday_school' 
                                ? <Church className="w-5 h-5 text-purple-600" />
                                : <School className="w-5 h-5 text-blue-600" />
                              }
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 hover:text-emerald-600 transition-colors">{org.name}</p>
                              <p className="text-xs text-slate-500">{org.contact_email}</p>
                            </div>
                          </Link>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            org.organization_type === 'church' ? 'bg-purple-100 text-purple-700' :
                            org.organization_type === 'sunday_school' ? 'bg-violet-100 text-violet-700' :
                            org.organization_type === 'school' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {org.organization_type?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>{org.teacher_count || 0}</TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>{org.student_count || 0}</TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>{org.class_count || 0}</TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <span className={`${(org.student_count || 0) > (org.student_licenses || 0) ? 'text-red-600' : 'text-slate-600'}`}>
                            {org.student_count || 0}/{org.student_licenses || 0}
                          </span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Link to={`/admin/organization/${org.id}`}>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`view-org-${org.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setAddUserDialog({ open: true, orgId: org.id, orgName: org.name })}
                              data-testid={`add-user-org-${org.id}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => setDeleteOrgDialog({ open: true, orgId: org.id, orgName: org.name })}
                              data-testid={`delete-org-${org.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Support Tickets Section */}
        {activeTab === 'support' && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-orange-500" />
                    Support Tickets
                  </CardTitle>
                  <CardDescription>Manage customer support requests</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={ticketFilter.status} onValueChange={(v) => setTicketFilter({ ...ticketFilter, status: v })}>
                    <SelectTrigger className="w-32" data-testid="ticket-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={ticketFilter.priority} onValueChange={(v) => setTicketFilter({ ...ticketFilter, priority: v })}>
                    <SelectTrigger className="w-32" data-testid="ticket-priority-filter">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchTickets} data-testid="refresh-tickets-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Ticket Stats */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{ticketStats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{ticketStats.open}</p>
                  <p className="text-xs text-slate-500">Open</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-600">{ticketStats.in_progress}</p>
                  <p className="text-xs text-slate-500">In Progress</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{ticketStats.resolved}</p>
                  <p className="text-xs text-slate-500">Resolved</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {ticketsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No support tickets found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subject</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id} data-testid={`ticket-row-${ticket.id}`}>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="font-medium truncate">{ticket.subject}</p>
                            <p className="text-xs text-slate-500 truncate">{ticket.description}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{ticket.user_name}</p>
                            <p className="text-xs text-slate-500">{ticket.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs capitalize">
                            {ticket.category?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                            ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {ticket.priority}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                            ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {ticket.status?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedTicket(ticket)}
                              data-testid={`view-ticket-${ticket.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600"
                              onClick={() => handleUpdateTicket(ticket.id, { status: 'resolved' })}
                              data-testid={`resolve-ticket-${ticket.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => handleDeleteTicket(ticket.id)}
                              data-testid={`delete-ticket-${ticket.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* FAQ Management Section */}
        {activeTab === 'faq' && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-cyan-500" />
                    Knowledge Base / FAQ
                  </CardTitle>
                  <CardDescription>Manage frequently asked questions</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedFaq} data-testid="seed-faq-btn">
                    <FileText className="w-4 h-4 mr-2" /> Seed Default FAQs
                  </Button>
                  <Button onClick={() => setShowNewFaqForm(true)} data-testid="add-faq-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add FAQ
                  </Button>
                </div>
              </div>
              
              {/* FAQ Stats */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{faqStats.total}</p>
                  <p className="text-xs text-slate-500">Total Articles</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{faqStats.published}</p>
                  <p className="text-xs text-slate-500">Published</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-600">{faqStats.draft}</p>
                  <p className="text-xs text-slate-500">Drafts</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{faqStats.total_views || 0}</p>
                  <p className="text-xs text-slate-500">Total Views</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* New FAQ Form */}
              {showNewFaqForm && (
                <Card className="mb-6 border-2 border-cyan-200 bg-cyan-50/30">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-lg">New FAQ Article</h3>
                      <Button size="sm" variant="ghost" onClick={() => setShowNewFaqForm(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Question *</Label>
                        <Input
                          placeholder="e.g., How do I add a child to my account?"
                          value={newFaq.question}
                          onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                          className="mt-1"
                          data-testid="new-faq-question"
                        />
                      </div>
                      <div>
                        <Label>Answer *</Label>
                        <Textarea
                          placeholder="Provide a detailed answer..."
                          value={newFaq.answer}
                          onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                          className="mt-1 min-h-24"
                          data-testid="new-faq-answer"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Category</Label>
                          <Select value={newFaq.category} onValueChange={(v) => setNewFaq({ ...newFaq, category: v })}>
                            <SelectTrigger className="mt-1" data-testid="new-faq-category">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="getting_started">Getting Started</SelectItem>
                              <SelectItem value="billing">Billing</SelectItem>
                              <SelectItem value="technical">Technical</SelectItem>
                              <SelectItem value="curriculum">Curriculum</SelectItem>
                              <SelectItem value="organizations">Organizations</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Order</Label>
                          <Input
                            type="number"
                            value={newFaq.order}
                            onChange={(e) => setNewFaq({ ...newFaq, order: parseInt(e.target.value) || 0 })}
                            className="mt-1"
                            data-testid="new-faq-order"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newFaq.is_published}
                              onChange={(e) => setNewFaq({ ...newFaq, is_published: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">Published</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setShowNewFaqForm(false)}>Cancel</Button>
                        <Button onClick={handleCreateFaq} disabled={savingFaq} data-testid="submit-new-faq">
                          {savingFaq && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Create FAQ
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {faqLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : faqArticles.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <HelpCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No FAQ articles yet</p>
                  <p className="text-sm mt-2">Click "Seed Default FAQs" to add starter content</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {faqArticles.map((faq) => (
                    <Card key={faq.id} className={`${!faq.is_published ? 'opacity-60 border-dashed' : ''}`} data-testid={`faq-card-${faq.id}`}>
                      <CardContent className="p-4">
                        {editingFaq?.id === faq.id ? (
                          <div className="space-y-3">
                            <Input
                              value={editingFaq.question}
                              onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                              placeholder="Question"
                            />
                            <Textarea
                              value={editingFaq.answer}
                              onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                              placeholder="Answer"
                              className="min-h-20"
                            />
                            <div className="flex gap-2">
                              <Select value={editingFaq.category} onValueChange={(v) => setEditingFaq({ ...editingFaq, category: v })}>
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="general">General</SelectItem>
                                  <SelectItem value="getting_started">Getting Started</SelectItem>
                                  <SelectItem value="billing">Billing</SelectItem>
                                  <SelectItem value="technical">Technical</SelectItem>
                                  <SelectItem value="curriculum">Curriculum</SelectItem>
                                  <SelectItem value="organizations">Organizations</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => handleUpdateFaq(faq.id, editingFaq)}>
                                <Save className="w-4 h-4 mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingFaq(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-slate-900">{faq.question}</h4>
                                {!faq.is_published && (
                                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Draft</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 whitespace-pre-wrap">{faq.answer}</p>
                              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                                <span className="px-2 py-0.5 bg-slate-100 rounded-full capitalize">{faq.category?.replace('_', ' ')}</span>
                                <span>{faq.views || 0} views</span>
                                <span className="text-green-600">{faq.helpful_count || 0} helpful</span>
                              </div>
                            </div>
                            <div className="flex gap-1 ml-4">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUpdateFaq(faq.id, { is_published: !faq.is_published })}
                                data-testid={`toggle-faq-${faq.id}`}
                              >
                                {faq.is_published ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4 text-slate-300" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingFaq({ ...faq })}
                                data-testid={`edit-faq-${faq.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => handleDeleteFaq(faq.id)}
                                data-testid={`delete-faq-${faq.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgDialog} onOpenChange={setCreateOrgDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-indigo-600" />
              Create New Organization
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Organization Name *</Label>
                <Input
                  placeholder="e.g., PS481 Brooklyn"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  className="mt-1"
                  data-testid="new-org-name"
                />
              </div>
              <div>
                <Label>Organization Type *</Label>
                <Select 
                  value={newOrg.organization_type} 
                  onValueChange={(v) => setNewOrg({ ...newOrg, organization_type: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="new-org-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="sunday_school">Sunday School</SelectItem>
                    <SelectItem value="church">Church</SelectItem>
                    <SelectItem value="homeschool">Homeschool Co-op</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Student Licenses</Label>
                <Input
                  type="number"
                  placeholder="50"
                  value={newOrg.student_licenses}
                  onChange={(e) => setNewOrg({ ...newOrg, student_licenses: e.target.value })}
                  className="mt-1"
                  data-testid="new-org-licenses"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-700">Organization Admin Account</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admin Name *</Label>
                  <Input
                    placeholder="John Smith"
                    value={newOrg.admin_name}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                    className="mt-1"
                    data-testid="new-org-admin-name"
                  />
                </div>
                <div>
                  <Label>Admin Email *</Label>
                  <Input
                    type="email"
                    placeholder="admin@school.edu"
                    value={newOrg.admin_email}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                    className="mt-1"
                    data-testid="new-org-admin-email"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Admin Password *</Label>
                  <Input
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newOrg.admin_password}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                    className="mt-1"
                    data-testid="new-org-admin-password"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-700">Contact Information (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    placeholder="contact@school.edu"
                    value={newOrg.contact_email}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={newOrg.contact_phone}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOrgDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrganization} disabled={creatingOrg} data-testid="submit-create-org">
              {creatingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Organization Details Dialog */}
      <Dialog open={viewOrgDialog} onOpenChange={setViewOrgDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedOrg?.organization?.organization_type === 'church' || selectedOrg?.organization?.organization_type === 'sunday_school'
                ? <Church className="w-5 h-5 text-purple-600" />
                : <School className="w-5 h-5 text-blue-600" />
              }
              {selectedOrg?.organization?.name}
            </DialogTitle>
          </DialogHeader>
          
          {orgDetailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : selectedOrg && (
            <div className="space-y-6 py-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedOrg.stats?.teachers || 0}</p>
                    <p className="text-xs text-slate-500">Teachers</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedOrg.stats?.students || 0}</p>
                    <p className="text-xs text-slate-500">Students</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{selectedOrg.stats?.classes || 0}</p>
                    <p className="text-xs text-slate-500">Classes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {selectedOrg.stats?.licenses_used || 0}/{selectedOrg.stats?.licenses_total || 0}
                    </p>
                    <p className="text-xs text-slate-500">Licenses</p>
                  </CardContent>
                </Card>
              </div>

              {/* Organization Details */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Organization Details</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Type:</span>
                    <span className="ml-2 capitalize">{selectedOrg.organization?.organization_type?.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Contact:</span>
                    <span className="ml-2">{selectedOrg.organization?.contact_email}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone:</span>
                    <span className="ml-2">{selectedOrg.organization?.contact_phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Onboarding:</span>
                    <span className={`ml-2 ${selectedOrg.organization?.onboarding_completed ? 'text-green-600' : 'text-amber-600'}`}>
                      {selectedOrg.organization?.onboarding_completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Users */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Users ({selectedOrg.users?.length || 0})</CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAddUserDialog({ open: true, orgId: selectedOrg.organization.id, orgName: selectedOrg.organization.name })}
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Add User
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrg.users?.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Select
                              value={user.account_type}
                              onValueChange={(v) => handleUpdateUserRole(user.id, v)}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="teacher">Teacher</SelectItem>
                                <SelectItem value="org_admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setResetPwdDialog({
                                  open: true,
                                  orgId: selectedOrg.organization.id,
                                  userId: user.id,
                                  userName: user.name
                                })}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => handleRemoveUserFromOrg(user.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Classes */}
              {selectedOrg.classes?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Classes ({selectedOrg.classes?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedOrg.classes?.map((cls) => (
                        <div key={cls.id} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium">{cls.name}</p>
                          <p className="text-sm text-slate-500">
                            {cls.student_ids?.length || 0} students • Grade: {cls.grade_level || 'N/A'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => setDeleteOrgDialog({
                open: true,
                orgId: selectedOrg?.organization?.id,
                orgName: selectedOrg?.organization?.name
              })}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete Organization
            </Button>
            <Button variant="outline" onClick={() => setViewOrgDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User to Organization Dialog */}
      <Dialog open={addUserDialog.open} onOpenChange={(open) => setAddUserDialog({ ...addUserDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User to {addUserDialog.orgName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="User's full name"
                value={newOrgUser.name}
                onChange={(e) => setNewOrgUser({ ...newOrgUser, name: e.target.value })}
                className="mt-1"
                data-testid="add-user-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@school.edu"
                value={newOrgUser.email}
                onChange={(e) => setNewOrgUser({ ...newOrgUser, email: e.target.value })}
                className="mt-1"
                data-testid="add-user-email"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="Minimum 6 characters"
                value={newOrgUser.password}
                onChange={(e) => setNewOrgUser({ ...newOrgUser, password: e.target.value })}
                className="mt-1"
                data-testid="add-user-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={newOrgUser.account_type}
                onValueChange={(v) => setNewOrgUser({ ...newOrgUser, account_type: v })}
              >
                <SelectTrigger className="mt-1" data-testid="add-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserDialog({ open: false, orgId: null, orgName: '' })}>
              Cancel
            </Button>
            <Button onClick={handleAddUserToOrg} disabled={addingUser} data-testid="submit-add-user">
              {addingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwdDialog.open} onOpenChange={(open) => setResetPwdDialog({ ...resetPwdDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Set a new password for <strong>{resetPwdDialog.userName}</strong>
            </p>
            <Label>New Password</Label>
            <Input
              type="password"
              placeholder="Minimum 6 characters"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className="mt-1"
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdDialog({ open: false, orgId: null, userId: null, userName: '' })}>
              Cancel
            </Button>
            <Button onClick={handleResetOrgUserPassword} disabled={resettingPwd} data-testid="submit-reset-pwd">
              {resettingPwd && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Organization Dialog */}
      <Dialog open={deleteOrgDialog.open} onOpenChange={(open) => setDeleteOrgDialog({ ...deleteOrgDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Organization</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <strong>{deleteOrgDialog.orgName}</strong>?
            </p>
            <p className="text-sm text-red-600 mt-2">
              This will permanently delete all associated users, students, classes, and data. This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrgDialog({ open: false, orgId: null, orgName: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteOrganization} disabled={deletingOrg} data-testid="confirm-delete-org">
              {deletingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Ticket Details Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Ticket Details
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Subject</Label>
                  <p className="font-medium">{selectedTicket.subject}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Select 
                    value={selectedTicket.status} 
                    onValueChange={(v) => handleUpdateTicket(selectedTicket.id, { status: v })}
                  >
                    <SelectTrigger className="w-full mt-1" data-testid="ticket-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">User</Label>
                  <p className="font-medium">{selectedTicket.user_name}</p>
                  <p className="text-xs text-slate-500">{selectedTicket.user_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <Select 
                    value={selectedTicket.priority} 
                    onValueChange={(v) => handleUpdateTicket(selectedTicket.id, { priority: v })}
                  >
                    <SelectTrigger className="w-full mt-1" data-testid="ticket-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Description</Label>
                <p className="mt-1 p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>
              
              {selectedTicket.admin_response && (
                <div>
                  <Label className="text-xs text-slate-500">Previous Response</Label>
                  <p className="mt-1 p-3 bg-green-50 rounded-lg text-sm whitespace-pre-wrap border-l-4 border-green-500">
                    {selectedTicket.admin_response}
                  </p>
                </div>
              )}
              
              <div>
                <Label>Reply to User</Label>
                <Textarea
                  placeholder="Type your response to the user..."
                  value={ticketResponse}
                  onChange={(e) => setTicketResponse(e.target.value)}
                  className="mt-1 min-h-24"
                  data-testid="ticket-response-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTicket(null)}>Close</Button>
            <Button onClick={handleRespondToTicket} disabled={respondingTicket} data-testid="send-ticket-response">
              {respondingTicket && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Send Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
